import React, { useMemo } from "react";
import {
    Modal as RNModal,
    ModalProps as RNModalProps,
    Platform,
    StyleSheet,
    useWindowDimensions,
    View,
} from "react-native";
import DropdownManager from "../contexts/DropdownManager";
import { useTheme } from "../hooks/use-theme";
import { SafeAreaProvider, SafeAreaView } from "react-native-safe-area-context";
import { LiftColors, theme } from "../theme";
import IconButton from "./IconButton";
import { useIsScreenSizeMin } from "../hooks/useIsScreenSizeMin";
import type { Edge, EdgeMode } from "react-native-safe-area-context/src/SafeArea.types";
import { KeyboardAvoidingView } from "react-native-keyboard-controller";
import { ThemeProvider } from "../contexts/ThemeManager";

export enum ModalSize {
    full,
    small,
}

type ModalProps = RNModalProps & {
    onClose?: () => void;
    size?: ModalSize;
    hideContainerBackground?: boolean;
    hideCloseButton?: boolean;
    fitContent?: boolean;
    bgColorName?: keyof LiftColors;
    edges?: Record<Edge, EdgeMode>;
    animationType?: "none" | "slide" | "fade";
};

const Modal = ({
    children,
    size = ModalSize.full,
    hideContainerBackground = false,
    onClose,
    hideCloseButton = false,
    fitContent = false,
    bgColorName,
    id,
    edges = {
        top: "additive",
        left: "additive",
        bottom: "additive",
        right: "additive",
    },
    animationType,
    ...props
}: ModalProps) => {
    const { colors, roundness } = useTheme();
    const styles = useStyles();
    const isLarge = useIsScreenSizeMin("large");

    const ModalAttributes: Record<
        ModalSize,
        { isTransparent: boolean; bgColorName: keyof LiftColors }
    > = {
        [ModalSize.full]: { isTransparent: isLarge, bgColorName: "surfaceVariant" },
        [ModalSize.small]: { isTransparent: true, bgColorName: "modalTransparentBackground" },
    };

    if (onClose && !hideCloseButton) {
        children = (
            <>
                {children}
                <IconButton style={styles.closeButton} onPress={onClose} icon="close" />
            </>
        );
    }
    const bgColor = bgColorName !== undefined ? colors[bgColorName] : undefined;
    const ModalContentComponent = (() => {
        switch (size) {
            case ModalSize.small:
                return SmallModalContent;
            default:
                return React.Fragment;
        }
    })();

    return (
        <ThemeProvider value={theme}>
            <RNModal
                transparent={ModalAttributes[size].isTransparent}
                animationType={animationType}
                {...props}
                onRequestClose={onClose}
            >
                <DropdownManager>
                    <SafeAreaProvider>
                        <SafeAreaView
                            edges={edges}
                            mode="padding"
                            style={{
                                flex: 1,
                                ...(isLarge && !hideContainerBackground
                                    ? {
                                          backgroundColor: "rgba(0, 0, 0, 0.3)",
                                          ...(Platform.OS === "web"
                                              ? {
                                                    backdropFilter: "blur(4px)",
                                                }
                                              : {}),
                                      }
                                    : {}),
                            }}
                        >
                            <KeyboardAvoidingView
                                id={id}
                                style={{
                                    ...(isLarge && size === ModalSize.full
                                        ? {
                                              margin: "auto",
                                              overflow: "hidden",
                                              boxShadow:
                                                  "rgba(17, 12, 46, 0.15) 0px 48px 100px 0px",
                                              backgroundColor:
                                                  props.transparent && hideContainerBackground
                                                      ? "transparent"
                                                      : "white",
                                              ...(props.transparent
                                                  ? {
                                                        height: "100%",
                                                        width: "100%",
                                                    }
                                                  : {
                                                        height: !fitContent ? "85%" : "auto",
                                                        width: 700,
                                                        maxHeight: 900,
                                                        borderRadius: roundness * 3,
                                                    }),
                                          }
                                        : { flex: 1 }),
                                    ...(bgColor
                                        ? {
                                              backgroundColor: bgColor,
                                          }
                                        : {}),
                                    display: "flex",
                                }}
                                behavior="padding"
                            >
                                <ModalContentComponent>{children}</ModalContentComponent>
                            </KeyboardAvoidingView>
                        </SafeAreaView>
                    </SafeAreaProvider>
                </DropdownManager>
            </RNModal>
        </ThemeProvider>
    );
};

type ModalContentProps = {
    children: React.ReactNode;
};

const SmallModalContent = ({ children }: ModalContentProps) => {
    const styles = useStyles();

    return <View style={styles.smallModalContainer}>{children}</View>;
};

const useStyles = () => {
    const { colors, shadow, marginSize } = useTheme();
    const { width, height } = useWindowDimensions();

    return useMemo(
        () =>
            StyleSheet.create({
                smallModalContainer: {
                    alignSelf: "stretch",
                    margin: 0,
                    width: width,
                    backgroundColor: colors.surfaceVariant,
                    maxHeight: height,
                    ...shadow,
                },
                closeButton: {
                    position: "absolute",
                    top: marginSize,
                    right: marginSize,
                },
            }),
        [width, colors.surfaceVariant, height, shadow, marginSize],
    );
};

export default Modal;
